import './header-slider.scss';

// Lightweight port of the legacy UIkit 2 data-uk-slideshow behaviour.
// Re-uses the existing markup (ul.uk-slideshow > li with uk-cover-background,
// [data-uk-slideshow-item="previous|next"]) so the dumped legacy CSS keeps
// working. Supports a cross-fade transition between slides, optional autoplay
// and an optional Ken Burns style zoom effect (continuous scale on the active
// background). Falls back gracefully when only a single slide is present.

const DEFAULT_AUTOPLAY_INTERVAL = 8000;
const DEFAULT_TRANSITION_DURATION = 1000;

const initSlideshow = ( root ) => {
	const list = root.querySelector( '.uk-slideshow' );
	const prev = root.querySelector( '[data-uk-slideshow-item="previous"]' );
	const next = root.querySelector( '[data-uk-slideshow-item="next"]' );

	if ( ! list ) {
		return;
	}

	const items = Array.from( list.children );
	if ( items.length === 0 ) {
		return;
	}

	const zoomEnabled = root.getAttribute( 'data-slider-zoom' ) === 'true';
	const autoplayInterval = Math.max(
		1000,
		parseInt( root.getAttribute( 'data-slider-autoplay-interval' ), 10 ) || DEFAULT_AUTOPLAY_INTERVAL
	);
	const transitionDuration = Math.max(
		100,
		parseInt( root.getAttribute( 'data-slider-duration' ), 10 ) || DEFAULT_TRANSITION_DURATION
	);

	let index = 0;
	let autoplayTimer = null;

	// Prime the items: position them all on top of each other, fade the active
	// one in. Styles are inlined so this works without component SCSS.
	items.forEach( ( item, itemIndex ) => {
		item.style.position = ( itemIndex === 0 ) ? 'relative' : 'absolute';
		item.style.top = '0';
		item.style.left = '0';
		item.style.right = '0';
		item.style.width = '100%';
		item.style.opacity = ( itemIndex === 0 ) ? '1' : '0';
		item.style.transition = `opacity ${ transitionDuration }ms ease-in-out`;
		item.setAttribute( 'aria-hidden', itemIndex === 0 ? 'false' : 'true' );
	} );

	list.style.position = 'relative';

	const setActive = ( target ) => {
		const total = items.length;
		const nextIndex = ( ( target % total ) + total ) % total;
		if ( nextIndex === index ) {
			return;
		}
		const current = items[ index ];
		const incoming = items[ nextIndex ];

		current.classList.remove( 'uk-active' );
		current.style.opacity = '0';
		current.setAttribute( 'aria-hidden', 'true' );

		incoming.classList.add( 'uk-active' );
		incoming.style.opacity = '1';
		incoming.setAttribute( 'aria-hidden', 'false' );

		if ( zoomEnabled ) {
			// Restart the CSS scale animation on the newly active slide.
			const bg = incoming.querySelector( '.uk-cover-background' );
			if ( bg ) {
				bg.style.animation = 'none';
				// Force reflow so the animation restarts from zero.
				// eslint-disable-next-line no-unused-expressions
				bg.offsetHeight;
				bg.style.animation = '';
			}
		}

		index = nextIndex;
	};

	const go = ( delta ) => setActive( index + delta );

	const stopAutoplay = () => {
		if ( autoplayTimer !== null ) {
			window.clearInterval( autoplayTimer );
			autoplayTimer = null;
		}
	};

	const startAutoplay = () => {
		if ( items.length <= 1 ) {
			return;
		}
		stopAutoplay();
		autoplayTimer = window.setInterval( () => go( 1 ), autoplayInterval );
	};

	if ( prev ) {
		prev.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			go( -1 );
			startAutoplay();
		} );
	}

	if ( next ) {
		next.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			go( 1 );
			startAutoplay();
		} );
	}

	root.addEventListener( 'mouseenter', stopAutoplay );
	root.addEventListener( 'mouseleave', startAutoplay );
	root.addEventListener( 'focusin', stopAutoplay );
	root.addEventListener( 'focusout', startAutoplay );

	document.addEventListener( 'visibilitychange', () => {
		if ( document.hidden ) {
			stopAutoplay();
		} else {
			startAutoplay();
		}
	} );

	startAutoplay();
};

document
	.querySelectorAll( '.js-headerSliderSlideshow' )
	.forEach( initSlideshow );
